import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { COLORS } from "../../constants/colors";
import {
  InputLabel,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Link,
  Button,
  CircularProgress,
  Alert
} from "@mui/material";

import moment from "moment";
import { Callout } from "components/Callout";
import { generateAutoOfferDescription, isPositiveNumber } from "./utils";

export const OfferModal = ({
  onChange,
  onImageUpload,
  offerImage,
  id,
  couponCode,
  offerTitle,
  offerDescription,
  offerUrl,
  offerStartDate,
  offerEndDate,
  dollarAmount,
  percentAmount,
  pointAmount,
  rewardRedemption,
  metadata = {},
  offer,
  isVisible,
  onSaveOffer,
  isSavingOffer,
  onUploadCoupons,
  vendorImageURL
}) => {
  const marketingImage = metadata?.marketingImage;
  const [csvUploadError, setCsvUploadError] = useState(null);
  const [csvUploadSuccess, setCsvUploadSuccess] = useState(false);
  const [isUploadingCoupons, setIsUploadingCoupons] = useState(false);
  const [pendingCouponCodes, setPendingCouponCodes] = useState([]);

  const isNewOffer = typeof id === "string" && id.startsWith("new");
  const hasPositiveDollarAmount = isPositiveNumber(dollarAmount);
  const hasPositivePercentAmount = isPositiveNumber(percentAmount);
  const hasConflictingAmounts = hasPositiveDollarAmount && hasPositivePercentAmount;
  const hasValidAmount = (hasPositiveDollarAmount || hasPositivePercentAmount) && !hasConflictingAmounts;
  const rewardRedemptionEnabled = rewardRedemption ?? true;

  const newOfferCalloutMessage = rewardRedemptionEnabled
    ? "Fill in the title, start date, and either a dollar or percent amount. Upload coupon codes."
    : "Fill in the title, start date, coupon code, and either a dollar or percent amount.";

  const existingOfferCalloutMessage = rewardRedemptionEnabled
    ? "Update this reward offer or upload additional coupon codes, and save your changes when you're ready."
    : "Update the offer details and save your changes when you're ready.";

  const handleCheckboxChange = (name, isChecked) => {
    if (
      isNewOffer &&
      name.startsWith("rewardRedemption:") &&
      isChecked === false
    ) {
      setPendingCouponCodes([]);
      onChange({
        target: {
          name: `couponCSV:${id}`,
          value: ""
        }
      });
    }
    onChange({
      target: {
        name,
        value: isChecked
      }
    });
  };

  const autoDescription = useMemo(() => {
    if (!hasValidAmount) {
      return "";
    }

    return generateAutoOfferDescription({
      dollarAmount,
      percentAmount,
      metadata
    });
  }, [dollarAmount, hasValidAmount, metadata, percentAmount]);

  const descriptionOverride = typeof offerDescription === "string" && offerDescription.trim().length > 0
    ? offerDescription
    : undefined;
  const displayDescription = descriptionOverride ?? autoDescription;
  const hasDescription = Boolean(displayDescription && displayDescription.trim().length > 0);

  const vendorImageProvided = Boolean(vendorImageURL);
  const offerImageProvided = Boolean(offerImage);
  const resolvedOfferImage = offerImageProvided
    ? offerImage
    : vendorImageProvided
      ? vendorImageURL
      : null;
  const usingVendorImageFallback = !offerImageProvided && Boolean(resolvedOfferImage);
  const marketingImageProvided = Boolean(marketingImage);
  const offerImageSatisfied = Boolean(resolvedOfferImage);
  const marketingImageRequired = rewardRedemptionEnabled;
  const marketingImageSatisfied = marketingImageRequired
    ? marketingImageProvided
    : true;
  const marketingImageLabel = marketingImageRequired
    ? "* Marketing Image Required"
    : marketingImageSatisfied
      ? "Optional"
      : "* Marketing Image Required";

  const offerImageBorderStyle = resolvedOfferImage
    ? `1px solid ${COLORS.WARM_GRAY}`
    : offerImageSatisfied
      ? `2px dashed ${COLORS.WARM_GRAY}`
      : `2px dashed ${COLORS.ERROR}`;
  const marketingImageBorderStyle = marketingImage
    ? `1px solid ${COLORS.WARM_GRAY}`
    : marketingImageSatisfied
      ? `2px dashed ${COLORS.WARM_GRAY}`
      : `2px dashed ${COLORS.ERROR}`;
  const offerImageMessageColor = offerImageSatisfied ? COLORS.WARM_GRAY : COLORS.ERROR;
  const marketingImageMessageColor = marketingImageSatisfied ? COLORS.WARM_GRAY : COLORS.ERROR;

  const getFormattedDateValue = (dateValue) => {
    if (!dateValue) {
      return "";
    }
    const parsed = moment(dateValue);
    if (!parsed.isValid()) {
      return "";
    }
    return parsed.format("YYYY-MM-DD");
  };

  const formattedStartDate = getFormattedDateValue(offerStartDate);
  const formattedEndDate = getFormattedDateValue(offerEndDate);
  const hasValidStartDate = formattedStartDate !== "";

  const rewardCouponsSatisfied = useMemo(() => {
    if (!rewardRedemptionEnabled) {
      return true;
    }
    if (isNewOffer) {
      return pendingCouponCodes.length > 0;
    }

    const existingCoupons =
      offer?.VendorOfferCoupons?.filter((coupon) => coupon.expired === false) ?? [];

    return existingCoupons.length > 0;
  }, [isNewOffer, offer?.VendorOfferCoupons, pendingCouponCodes.length, rewardRedemptionEnabled]);

  const areNewOfferFieldsMissing = !offerTitle || !hasDescription || !hasValidStartDate || !offerImageSatisfied || !marketingImageSatisfied || !rewardCouponsSatisfied;
  const areExistingOfferFieldsMissing = !offerTitle || !hasDescription || !hasValidStartDate || !offerImageSatisfied || !marketingImageSatisfied;
  const parseCouponCodesFromCSV = (csvContent) => {
    const rows = csvContent
      .split(/\r?\n/)
      .map((row) => row.trim())
      .filter((row) => row.length > 0);

    if (rows.length < 2) {
      throw new Error("CSV must contain a header and at least one coupon code.");
    }

    const rowSplitRegex = /,(?=(?:(?:[^"]*"){2})*[^"]*$)/;
    const headers = rows[0]
      .split(rowSplitRegex)
      .map((header) => header.trim().replace(/^"(.*)"$/, "$1").toLowerCase());

    const possibleHeaders = ["couponcode", "coupon_code", "code"];
    const codeIndex = headers.findIndex((header) => possibleHeaders.includes(header));

    if (codeIndex === -1) {
      throw new Error(`CSV must have a column named 'couponCode', 'coupon_code', or 'code'. Found columns: ${headers.join(", ")}`);
    }

    const couponCodes = rows.slice(1).map((row) => {
      const values = row
        .split(rowSplitRegex)
        .map((value) => value.trim().replace(/^"(.*)"$/, "$1"));
      return values[codeIndex];
    })
      .filter((code) => typeof code === "string" && code.trim().length > 0)
      .map((code) => code.trim());

    if (couponCodes.length === 0) {
      throw new Error("No valid coupon codes found in CSV.");
    }

    const seen = new Set();
    for (const code of couponCodes) {
      const normalized = code.toLowerCase();
      if (seen.has(normalized)) {
        throw new Error("CSV contains duplicate coupon codes.");
      }
      seen.add(normalized);
    }

    return couponCodes;
  };

  const handleCsvUpload = async (e) => {
    setCsvUploadError(null);
    setCsvUploadSuccess(false);
    const file = e.target.files?.[0];
    if (!file) { return; }

    setIsUploadingCoupons(true);
    try {
      // Read file content
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = (error) => reject(error);
        reader.readAsText(file);
      });

      if (rewardRedemptionEnabled && isNewOffer) {
        const couponCodes = parseCouponCodesFromCSV(fileContent);
        setPendingCouponCodes(couponCodes);
        onChange({
          target: {
            name: `couponCSV:${id}`,
            value: fileContent
          }
        });
        setCsvUploadSuccess(true);
      } else if (onUploadCoupons) {
        await onUploadCoupons(fileContent, id);
        setCsvUploadSuccess(true);
      } else {
        throw new Error("Uploading coupons is not supported for this offer.");
      }
      e.target.value = "";
      setTimeout(() => setCsvUploadSuccess(false), 5000);
    } catch (error) {
      setCsvUploadError(error.message || "Failed to upload coupons");
      // Clear the file input on error too
      e.target.value = "";
    } finally {
      setIsUploadingCoupons(false);
    }
  };

  return (
    <Box padding={6} flexDirection="column" display="flex">
      <Callout type="info" maxWidth="100%">
        {isNewOffer ? newOfferCalloutMessage : existingOfferCalloutMessage}
      </Callout>
      <Box paddingBottom={2} />

      {isNewOffer && (
        <>
          <Button
            variant="contained"
            color="primary"
            onClick={onSaveOffer}
            disabled={isSavingOffer || areNewOfferFieldsMissing || !hasValidAmount}
            fullWidth
            sx={{ mb: 2 }}
          >
            {isSavingOffer ? <CircularProgress size={24} /> : "Save Offer"}
          </Button>
          {(areNewOfferFieldsMissing || !hasValidAmount) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {"Title, description,start date, and exactly one positive dollar or percent amount are required. Upload offer and marketing images only when this is a non-reward offer."}
            </Alert>
          )}
        </>
      )}

      {!isNewOffer && onSaveOffer && (
        <Button
          variant="contained"
          color="primary"
          onClick={onSaveOffer}
          disabled={isSavingOffer || areExistingOfferFieldsMissing || !hasValidAmount}
          fullWidth
          sx={{ mb: 2 }}
        >
          {isSavingOffer ? <CircularProgress size={24} /> : "Save Changes"}
        </Button>
      )}
      <TextField
        label="Title"
        name={`offerTitle:${id}`}
        value={offerTitle}
        onChange={onChange}
        required
      />
      <Box paddingBottom={2} />
      <Box paddingBottom={2} display="flex" flexDirection="row" alignItems="flex-end" gap={2}>
        <Box flex={1}>
          <TextField
            label="Dollar Amount"
            name={`dollarAmount:${id}`}
            value={dollarAmount ?? ""}
            onChange={onChange}
            type="number"
            inputProps={{ step: "0.1", min: "0.01" }}
            placeholder="0.00"
            error={hasConflictingAmounts}
            helperText={hasConflictingAmounts ? "Choose either dollar or percent amount, not both." : undefined}
            fullWidth
          />
        </Box>
        <Box flex={1}>
          <TextField
            label="Percent Amount"
            name={`percentAmount:${id}`}
            value={percentAmount ?? ""}
            onChange={onChange}
            type="number"
            inputProps={{ step: "0.1", min: "0.01", max: "100" }}
            error={hasConflictingAmounts}
            placeholder="0.00"
            helperText={hasConflictingAmounts ? "Choose either dollar or percent amount, not both." : undefined}
            fullWidth
          />
        </Box>

        <Box flex={1}>

          <TextField
            fullWidth
            label="Min. Purchase Amount: Optional"
            name={`metadata.voucherMinimumPurchase:${id}`}
            type="number"
            value={metadata?.voucherMinimumPurchase ?? ""}
            inputProps={{ step: "0.1", min: "0.01" }}
            onChange={onChange}
          />
        </Box>
      </Box>
      <TextField
        label="Description"
        name={`offerDescription:${id}`}
        value={displayDescription ?? ""}
        onChange={onChange}
        required
        helperText={autoDescription
          ? `Auto-generated: ${autoDescription}; type to override.`
          : undefined}
      />
      <Box paddingBottom={2} />
      <Tooltip title="For offer use LCM. Auto-filled with vendor website URL for new offers, but can be customized.">
        <TextField
          label="Offer Link: Optional; Defaults to vendor website URL"
          name={`offerUrl:${id}`}
          value={offerUrl || ""}
          onChange={onChange}
          fullWidth
        />
      </Tooltip>
      <Box paddingBottom={2} />

      <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="flex-end" gap={2} paddingBottom={2}>
        <FormControlLabel
          control={
            <Checkbox
              name={`isVisible:${id}`}
              checked={isVisible ?? false}
              onChange={(_, checked) => handleCheckboxChange(`isVisible:${id}`, checked)}
            />
          }
          label="Visible to users in app"
        />
        <Box flex={1}>
          <InputLabel>Start Date *</InputLabel>
          <input
            name={`offerStartDate:${id}`}
            value={formattedStartDate}
            type="date"
            onChange={onChange}
            required
          />
        </Box>
        <Box flex={1}>
          <InputLabel>End Date: Optional</InputLabel>
          <input
            name={`offerEndDate:${id}`}
            value={formattedEndDate}
            type="date"
            onChange={onChange}
          />
        </Box>

      </Box>

      <Tooltip title="Displays on app offer checkout page.">
        <TextField
          label="Offer Fine Print: Optional"
          name={`metadata.voucherFinePrint:${id}`}
          value={metadata?.voucherFinePrint || ""}
          onChange={onChange}
          multiline
          rows={2}
        />
      </Tooltip>
      <Box paddingBottom={2} />

      <FormControlLabel
        control={
          <Checkbox
            name={`rewardRedemption:${id}`}
            checked={rewardRedemptionEnabled}
            onChange={(_, checked) => handleCheckboxChange(`rewardRedemption:${id}`, checked)}
          />
        }
        label="Enable reward redemption"
      />
      {rewardRedemptionEnabled
        ? (
          <>
            <Box flex={1}>
              <Tooltip title="Reward points/seeds value needed for reward redemption">
                <TextField
                  label="Point Amount (Seeds)"
                  name={`pointAmount:${id}`}
                  value={pointAmount || ""}
                  onChange={onChange}
                  type="number"
                  inputProps={{ min: "0" }}
                  fullWidth
                  required={rewardRedemptionEnabled}
                />
              </Tooltip>
            </Box>
            <Box paddingBottom={2} />
            <Typography variant="h6" gutterBottom>
            Coupon Codes
            </Typography>
          </>
        )
        : (
          <Box flex={1}>
            <TextField
              label="Coupon Code"
              name={`couponCode:${id}`}
              value={couponCode || ""}
              onChange={onChange}
              fullWidth
              required={!rewardRedemptionEnabled}
            />
          </Box>
        )}

      {rewardRedemptionEnabled && (
        <>
          <Box paddingBottom={2} />
          <Callout type="warning" maxWidth="100%">
            Upload coupon codes as a CSV file. Reward offers must include at least one coupon before you can save changes.
          </Callout>
          <Box paddingBottom={2} />

          {csvUploadSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {isNewOffer
                ? "Coupon codes ready. They will be saved with this offer."
                : "Coupon codes uploaded successfully!"}
            </Alert>
          )}

          {csvUploadError && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                maxWidth: "100%",
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
                maxHeight: "200px",
                overflowY: "auto"
              }}
            >
              {csvUploadError}
            </Alert>
          )}

          <Box
            sx={{
              border: `2px dashed ${COLORS.WARM_GRAY}`,
              borderRadius: "8px",
              padding: 3,
              backgroundColor: "#f9f9f9",
              position: "relative",
              cursor: isUploadingCoupons ? "not-allowed" : "pointer",
              opacity: isUploadingCoupons ? 0.6 : 1
            }}
          >
            <input
              type="file"
              accept=".csv"
              name={`couponCsv:${id}`}
              onChange={handleCsvUpload}
              disabled={isUploadingCoupons}
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                opacity: 0,
                cursor: isUploadingCoupons ? "not-allowed" : "pointer",
                top: 0,
                left: 0
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center"
              }}
            >
              {isUploadingCoupons
                ? (
                  <>
                    <CircularProgress size={24} sx={{ mb: 1 }} />
                    <Typography variant="body1">Processing coupons...</Typography>
                  </>
                )
                : (
                  <>
                    <Typography variant="body1" gutterBottom>
                      Click to upload CSV file
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      CSV format: Single column with header &quot;couponCode&quot;, one code per row
                    </Typography>
                  </>
                )}
            </Box>
          </Box>

          {isNewOffer && pendingCouponCodes.length > 0 && (
            <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
              {pendingCouponCodes.length} coupon code{pendingCouponCodes.length > 1 ? "s" : ""} ready to be saved with this offer.
            </Typography>
          )}

          <Box paddingBottom={2} />
        </>
      )}

      {offer && offer.VendorOfferCoupons && offer.VendorOfferCoupons.length > 0 && (
        <>
          <Box paddingBottom={4} />
          <Typography variant="h6" gutterBottom>
            All Coupon Codes ({offer.VendorOfferCoupons.length})
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Coupon Code</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Redeemed By (User ID)</strong></TableCell>
                  <TableCell><strong>Coupon Created On</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {offer.VendorOfferCoupons.map((coupon) => {
                  const redemptions = coupon.UserVendorOfferCouponRedemptions || [];
                  return (
                    <TableRow key={coupon.id}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {coupon.couponCode || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {coupon.expired
                          ? (
                            <Chip label="Redeemed" size="small" color="error" />
                          )
                          : (
                            <Chip label="Available" size="small" color="default" />
                          )}
                      </TableCell>
                      <TableCell>
                        {coupon.expired
                          ? (
                            <Box>
                              {redemptions.length > 0
                                ? redemptions.map((redemption, index) => (
                                  <Link
                                    key={index}
                                    href={`/users/${redemption.userId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      display: "block",
                                      mb: index < redemptions.length - 1 ? 0.5 : 0
                                    }}
                                  >
                                    {redemption.userId}
                                  </Link>
                                ))
                                : (
                                  <Typography variant="body2" color="warning.main">
                                    No redemption record found
                                  </Typography>
                                )}
                            </Box>
                          )
                          : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {coupon.createdAt ? moment(coupon.createdAt).format("MMM D, YYYY") : "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Box paddingBottom={2} />
      <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="flex-end" gap={2}>
        <Box flex={1}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Offer Image. (Defaults to Vendor Image; can override)</Typography>
          <Box
            mb={4}
            sx={{
              width: "100%",
              borderRadius: "8px",
              border: offerImageBorderStyle,
              overflow: "hidden",
              position: "relative",
              backgroundColor: offerImage ? "transparent" : offerImageSatisfied ? "#f9f9f9" : "#fff5f5",
              "::after": {
                display: "block",
                content: '""',
                paddingBottom: "56.25%"
              }
            }}
          >
            <Box
              as="input"
              type="file"
              sx={{
                position: "absolute",
                width: "100%",
                height: "100%",
                opacity: "0",
                cursor: "pointer",
                zIndex: "2"
              }}
              onChange={onImageUpload}
              accept="image/jpeg"
              name={`offerImage:${id}`}
            />
            {resolvedOfferImage
              ? (
                <Box
                  as="img"
                  src={resolvedOfferImage}
                  name={`offerImage:${id}`}
                  alt={offerTitle || "Offer image"}
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    zIndex: "1"
                  }}
                />
              )
              : (
                <Box
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: "1",
                    color: offerImageMessageColor,
                    textAlign: "center",
                    padding: 2
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {offerImageSatisfied
                      ? usingVendorImageFallback
                        ? "Using vendor image"
                        : "Optional"
                      : "* Image Required"}
                  </Typography>
                  <Typography variant="body2">
                    Click to upload a JPEG image
                  </Typography>
                  <Typography variant="caption" sx={{ mt: 1, opacity: 0.7 }}>
                    {offerImageSatisfied && !usingVendorImageFallback
                      ? "Upload to override the vendor cover image"
                      : "Use Figma template"}
                  </Typography>
                </Box>
              )}
          </Box>
        </Box>
        {rewardRedemptionEnabled && (
          <Box flex={1}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Marketing Image</Typography>
            <Box
              mb={4}
              sx={{
                width: "100%",
                borderRadius: "8px",
                border: marketingImageBorderStyle,
                overflow: "hidden",
                position: "relative",
                backgroundColor: marketingImage ? "transparent" : marketingImageSatisfied ? "#f9f9f9" : "#fff5f5",
                "::after": {
                  display: "block",
                  content: '""',
                  paddingBottom: "56.25%"
                }
              }}
            >
              <Box
                as="input"
                type="file"
                sx={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  opacity: "0",
                  cursor: "pointer",
                  zIndex: "2"
                }}
                onChange={onImageUpload}
                accept="image/png"
                name={`metadata.marketingImage:${id}`}
              />
              {marketingImage
                ? (
                  <Box
                    as="img"
                    src={marketingImage}
                    name={`metadata.marketingImage:${id}`}
                    alt="Marketing image"
                    style={{
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      zIndex: "1"
                    }}
                  />
                )
                : (
                  <Box
                    sx={{
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: "1",
                      color: marketingImageMessageColor,
                      textAlign: "center",
                      padding: 2
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {marketingImageLabel}
                    </Typography>
                    <Typography variant="body2">
                      Click to upload a PNG image
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 1, opacity: 0.7 }}>
                      <Link
                        href="https://www.figma.com/design/DBieyLhOVADCD5gQh6Zowt/Lifecycle-Marketing?node-id=13679-27546&p=f&t=kl5QlSPiD44GKvhm-0"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Use LCM Figma template
                      </Link>
                    </Typography>
                  </Box>
                )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
